import type {
  AdminIdentity,
  Business,
  Channel,
  ChannelConsumerLimitView,
  ChannelInput,
  CircuitView,
  Consumer,
  ConsumerCopyInput,
  ConsumerChannelLimit,
  ConsumerChannelLimitView,
  ConsumerInput,
  DownstreamModelOption,
  DashboardSummary,
  GatewayModel,
  GatewayConfigurationOptions,
  ProviderMappingCatalog,
  GatewayRequestListItem,
  GatewayRequestDetail,
  GatewayRequestPayloadDetail,
  MutationResult,
  ModelInput,
  OAuthConfig,
  PlaygroundCapabilities,
  PlaygroundGeneration,
  PlaygroundHistoryPage,
  PlaygroundHistoryScope,
  PlaygroundImageInput,
  PlaygroundSubmission,
  PlaygroundUpload,
  PlaygroundVideoInput,
  Policy,
  Price,
  ProviderCatalog,
  ProviderChannel,
  ProviderChannelCapacityInput,
  ConfirmedDeleteInput,
  ProviderChannelInput,
  ProviderCredential,
  ProviderInput,
  ProviderMappingInput,
  ReorderProviderMappingsInput,
  ModelProviderMapping,
  MappingPriceInput,
  ManagedStatusInput,
  ProviderProbeResult,
  ProviderChannelPool,
  ProviderChannelPoolInput,
  ProviderChannelPoolImportResult,
  UsageReport,
} from "./types";
import type { RuntimeChannelDetail, RuntimeChannelList, RuntimeModelDetail, RuntimeModelList } from "./runtimeTypes";

function listOrEmpty<T>(value: T[] | null | undefined): T[] {
  return Array.isArray(value) ? value : [];
}

function normalizeChannel(channel: Channel): Channel {
  return { ...channel, prices: listOrEmpty(channel.prices) };
}

function normalizeModel(model: GatewayModel): GatewayModel {
  return { ...model, channels: listOrEmpty(model.channels).map(normalizeChannel) };
}

function normalizeBusiness(business: Business): Business {
  return { ...business, policies: listOrEmpty(business.policies) };
}

function normalizeConsumer(consumer: Consumer): Consumer {
  return {
    ...consumer,
    keys: listOrEmpty(consumer.keys),
    policies: listOrEmpty(consumer.policies),
    businesses: listOrEmpty(consumer.businesses).map(normalizeBusiness),
  };
}

export class ApiError extends Error {
  status: number;
  // code is the optional machine-readable admin error code, e.g. PLAYGROUND_NOT_CONFIGURED.
  // code 为可选的管理端错误码，例如 PLAYGROUND_NOT_CONFIGURED。
  code: string;

  constructor(status: number, message: string, code = "") {
    super(message);
    this.status = status;
    this.code = code;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  // FormData must keep the browser-generated multipart boundary, so only JSON bodies get a Content-Type.
  // FormData 需要浏览器自动生成 multipart 边界，只有 JSON 请求体才设置 Content-Type。
  const jsonBody = Boolean(init?.body) && !(init?.body instanceof FormData);
  const response = await fetch(path, {
    credentials: "include",
    ...init,
    headers: {
      Accept: "application/json",
      ...(jsonBody ? { "Content-Type": "application/json" } : {}),
      ...init?.headers,
    },
  });

  const text = await response.text();
  let payload: Record<string, unknown> = {};
  if (text) {
    try {
      payload = JSON.parse(text) as Record<string, unknown>;
    } catch {
      throw new ApiError(response.status, response.ok ? "服务返回了无法解析的响应" : `请求失败 (${response.status})`);
    }
  }
  if (!response.ok) {
    if (response.status === 401 && !path.startsWith("/api/admin/oauth/") && typeof window !== "undefined") {
      const returnTo = `${window.location.pathname}${window.location.search}`;
      window.location.replace(`/login?returnTo=${encodeURIComponent(returnTo)}`);
    }
    const message = String(payload.detail ?? payload.error ?? `请求失败 (${response.status})`);
    throw new ApiError(response.status, message, typeof payload.code === "string" ? payload.code : "");
  }
  return payload as T;
}

export const api = {
  oauthConfig() {
    return request<OAuthConfig>("/api/admin/oauth/config");
  },
  oauthExchange(code: string) {
    return request<AdminIdentity & { success: boolean }>("/api/admin/oauth/exchange", {
      method: "POST",
      body: JSON.stringify({ code }),
    });
  },
  me() {
    return request<AdminIdentity>("/api/admin/me");
  },
  logout() {
    return request<{ success: boolean }>("/api/admin/logout", { method: "POST" });
  },
  configurationOptions() {
    return request<{ data: GatewayConfigurationOptions }>("/admin-api/gateway/options");
  },
  runtimeModels(signal?: AbortSignal) {
    return request<{ data: RuntimeModelList }>("/admin-api/gateway/runtime/models", { signal });
  },
  runtimeModel(id: number, signal?: AbortSignal) {
    return request<{ data: RuntimeModelDetail }>(`/admin-api/gateway/runtime/models/${id}`, { signal });
  },
  runtimeChannels(signal?: AbortSignal) {
    return request<{ data: RuntimeChannelList }>("/admin-api/gateway/runtime/channels", { signal });
  },
  runtimeChannel(id: number, signal?: AbortSignal) {
    return request<{ data: RuntimeChannelDetail }>(`/admin-api/gateway/runtime/channels/${id}`, { signal });
  },
  async models() {
    const response = await request<{ data: GatewayModel[] }>("/admin-api/gateway/models");
    return { ...response, data: listOrEmpty(response.data).map(normalizeModel) };
  },
  async providerMappings(): Promise<{ data: ProviderMappingCatalog }> {
    const response = await request<{ data: ProviderMappingCatalog }>("/admin-api/gateway/provider-mappings");
    return {
      ...response,
      data: {
        ...response.data,
        models: listOrEmpty(response.data.models),
        providers: listOrEmpty(response.data.providers),
        channels: listOrEmpty(response.data.channels),
        mappings: listOrEmpty(response.data.mappings),
        prices: listOrEmpty(response.data.prices),
        credentials: listOrEmpty(response.data.credentials),
        pools: listOrEmpty(response.data.pools),
        pool_members: listOrEmpty(response.data.pool_members),
      },
    };
  },
  createProvider(input: ProviderInput) {
    return request<MutationResult<ProviderCatalog>>("/admin-api/gateway/providers", { method: "POST", body: JSON.stringify(input) });
  },
  createProviderChannelPool(input: ProviderChannelPoolInput) {
    return request<MutationResult<ProviderChannelPool>>("/admin-api/gateway/provider-channel-pools", { method: "POST", body: JSON.stringify(input) });
  },
  updateProviderChannelPool(id: number, input: ProviderChannelPoolInput) {
    return request<MutationResult<ProviderChannelPool>>(`/admin-api/gateway/provider-channel-pools/${id}`, { method: "PUT", body: JSON.stringify(input) });
  },
  updateProviderChannelPoolStatus(id: number, input: ManagedStatusInput) {
    return request<MutationResult<ProviderChannelPool>>(`/admin-api/gateway/provider-channel-pools/${id}/status`, { method: "PATCH", body: JSON.stringify(input) });
  },
  updateProviderChannelPoolMemberStatus(poolId: number, channelId: number, input: ManagedStatusInput) {
    return request<MutationResult<unknown>>(`/admin-api/gateway/provider-channel-pools/${poolId}/members/${channelId}/status`, { method: "PATCH", body: JSON.stringify(input) });
  },
  importAzureProviderChannelPool(id: number, content: string, dryRun: boolean, allowRotation = false, templateChannelId = 0) {
    return request<MutationResult<ProviderChannelPoolImportResult>>(`/admin-api/gateway/provider-channel-pools/${id}/import`, {
      method: "POST",
      body: JSON.stringify({ content, dry_run: dryRun, allow_rotation: allowRotation, template_channel_id: templateChannelId || undefined }),
    });
  },
  probeProviderChannelPool(id: number) {
    return request<{ data: { pool_id: number; succeeded: number; failed: number } }>(`/admin-api/gateway/provider-channel-pools/${id}/probe`, { method: "POST" });
  },
  updateProvider(id: number, input: ProviderInput) {
    return request<MutationResult<ProviderCatalog>>(`/admin-api/gateway/providers/${id}`, { method: "PUT", body: JSON.stringify(input) });
  },
  updateProviderStatus(id: number, input: ManagedStatusInput) {
    return request<MutationResult<ProviderCatalog>>(`/admin-api/gateway/providers/${id}/status`, { method: "PATCH", body: JSON.stringify(input) });
  },
  deleteProvider(id: number, input: ConfirmedDeleteInput) {
    return request<MutationResult<ProviderCatalog>>(`/admin-api/gateway/providers/${id}`, { method: "DELETE", body: JSON.stringify(input) });
  },
  createProviderChannel(input: ProviderChannelInput) {
    return request<MutationResult<ProviderChannel>>("/admin-api/gateway/provider-channels", { method: "POST", body: JSON.stringify(input) });
  },
  updateProviderChannel(id: number, input: ProviderChannelInput) {
    return request<MutationResult<ProviderChannel>>(`/admin-api/gateway/provider-channels/${id}`, { method: "PUT", body: JSON.stringify(input) });
  },
  updateProviderChannelCapacity(id: number, input: ProviderChannelCapacityInput) {
    return request<MutationResult<ProviderChannel>>(`/admin-api/gateway/provider-channels/${id}/capacity`, { method: "PATCH", body: JSON.stringify(input) });
  },
  deleteProviderChannel(id: number, input: ConfirmedDeleteInput) {
    return request<MutationResult<ProviderChannel>>(`/admin-api/gateway/provider-channels/${id}`, { method: "DELETE", body: JSON.stringify(input) });
  },
  updateProviderChannelStatus(id: number, input: ManagedStatusInput) {
    return request<MutationResult<ProviderChannel>>(`/admin-api/gateway/provider-channels/${id}/status`, { method: "PATCH", body: JSON.stringify(input) });
  },
  setProviderCredential(id: number, credential: string) {
    return request<MutationResult<ProviderCredential>>(`/admin-api/gateway/provider-channels/${id}/credential`, { method: "PUT", body: JSON.stringify({ credential }) });
  },
  installTencentEnvironmentCredential(id: number, expectedUpdatedAt: number) {
    return request<MutationResult<ProviderCredential>>(`/admin-api/gateway/provider-channels/${id}/tencent-environment-credential`, {
      method: "POST",
      body: JSON.stringify({ expected_updated_at: expectedUpdatedAt, confirm: true }),
    });
  },
  revokeProviderCredential(id: number, reason: string) {
    return request<MutationResult<ProviderCredential>>(`/admin-api/gateway/provider-channels/${id}/credential`, { method: "DELETE", body: JSON.stringify({ reason, confirm: true }) });
  },
  probeProviderChannel(id: number) {
    return request<{ data: ProviderProbeResult }>(`/admin-api/gateway/provider-channels/${id}/probe`, { method: "POST" });
  },
  createProviderMapping(input: ProviderMappingInput) {
    return request<MutationResult<ModelProviderMapping>>("/admin-api/gateway/provider-mappings", { method: "POST", body: JSON.stringify(input) });
  },
  updateProviderMapping(id: number, input: ProviderMappingInput) {
    return request<MutationResult<ModelProviderMapping>>(`/admin-api/gateway/provider-mappings/${id}`, { method: "PUT", body: JSON.stringify(input) });
  },
  reorderProviderMappings(modelId: number, input: ReorderProviderMappingsInput) {
    return request<MutationResult<ModelProviderMapping[]>>(`/admin-api/gateway/models/${modelId}/provider-mappings/order`, { method: "PUT", body: JSON.stringify(input) });
  },
  updateProviderMappingStatus(id: number, input: ManagedStatusInput) {
    return request<MutationResult<ModelProviderMapping>>(`/admin-api/gateway/provider-mappings/${id}/status`, { method: "PATCH", body: JSON.stringify(input) });
  },
  replaceProviderMappingPrices(id: number, expectedUpdatedAt: number, prices: MappingPriceInput[]) {
    return request<MutationResult<unknown[]>>(`/admin-api/gateway/provider-mappings/${id}/prices`, {
      method: "PUT", body: JSON.stringify({ expected_updated_at: expectedUpdatedAt, prices }),
    });
  },
  async model(id: number) {
    const response = await request<{ data: GatewayModel }>(`/admin-api/gateway/models/${id}`);
    return { ...response, data: normalizeModel(response.data) };
  },
  createModel(input: ModelInput) {
    return request<MutationResult<GatewayModel>>("/admin-api/gateway/models", { method: "POST", body: JSON.stringify(input) });
  },
  updateModel(id: number, input: ModelInput) {
    return request<MutationResult<GatewayModel>>(`/admin-api/gateway/models/${id}`, { method: "PUT", body: JSON.stringify(input) });
  },
  async channel(id: number) {
    const response = await request<{ data: Channel }>(`/admin-api/gateway/channels/${id}`);
    return { ...response, data: normalizeChannel(response.data) };
  },
  createChannel(modelId: number, input: ChannelInput) {
    return request<MutationResult<Channel>>(`/admin-api/gateway/models/${modelId}/channels`, { method: "POST", body: JSON.stringify(input) });
  },
  updateChannel(id: number, input: ChannelInput) {
    return request<MutationResult<Channel>>(`/admin-api/gateway/channels/${id}`, { method: "PUT", body: JSON.stringify(input) });
  },
  replacePrices(id: number, expectedUpdatedAt: number, prices: Array<Pick<Price, "billing_unit" | "resolution_key" | "unit_price" | "currency">>) {
    return request<MutationResult<Channel>>(`/admin-api/gateway/channels/${id}/prices`, {
      method: "PUT",
      body: JSON.stringify({ expected_channel_updated_at: expectedUpdatedAt, prices }),
    });
  },
  reorderChannels(modelId: number, expectedUpdatedAt: number, channelIds: number[]) {
    return request<MutationResult<GatewayModel>>(`/admin-api/gateway/models/${modelId}/channels/order`, {
      method: "PUT",
      body: JSON.stringify({
        expected_model_updated_at: expectedUpdatedAt,
        channel_ids: channelIds,
      }),
    });
  },
  async consumers() {
    const response = await request<{ data: Consumer[] }>("/admin-api/gateway/consumers");
    return { ...response, data: listOrEmpty(response.data).map(normalizeConsumer) };
  },
  async consumer(id: number) {
    const response = await request<{ data: Consumer }>(`/admin-api/gateway/consumers/${id}`);
    return { ...response, data: normalizeConsumer(response.data) };
  },
  downstreamOptions(): Promise<{ data: DownstreamModelOption[] }> {
    return request<{ data: DownstreamModelOption[] }>("/admin-api/gateway/downstream-options").then((response) => ({
      ...response,
      data: listOrEmpty(response.data).map((model) => ({ ...model, routes: listOrEmpty(model.routes), pools: listOrEmpty(model.pools) })),
    }));
  },
  createConsumer(input: ConsumerInput) {
    return request<MutationResult<Consumer>>("/admin-api/gateway/consumers", { method: "POST", body: JSON.stringify(input) });
  },
  copyConsumer(id: number, input: ConsumerCopyInput) {
    return request<MutationResult<Consumer>>(`/admin-api/gateway/consumers/${id}/copy`, { method: "POST", body: JSON.stringify(input) });
  },
  updateConsumer(id: number, input: ConsumerInput) {
    return request<MutationResult<Consumer>>(`/admin-api/gateway/consumers/${id}`, { method: "PUT", body: JSON.stringify(input) });
  },
  deleteConsumer(id: number, input: ConfirmedDeleteInput) {
    return request<MutationResult<Consumer>>(`/admin-api/gateway/consumers/${id}`, { method: "DELETE", body: JSON.stringify(input) });
  },
  consumerChannelLimits(id: number) {
    return request<{ data: ConsumerChannelLimitView[] }>(`/admin-api/gateway/consumers/${id}/channel-limits`);
  },
  updateConsumerChannelLimit(consumerId: number, channelId: number, input: {
    qps_limit: number;
    max_concurrency: number;
    expected_updated_at?: number;
  }) {
    return request<MutationResult<ConsumerChannelLimit>>(`/admin-api/gateway/consumers/${consumerId}/channel-limits/${channelId}`, {
      method: "PUT",
      body: JSON.stringify(input),
    });
  },
  channelConsumerLimits(id: number) {
    return request<{ data: ChannelConsumerLimitView[] }>(`/admin-api/gateway/channels/${id}/consumer-limits`);
  },
  rotateKey(id: number) {
    return request<MutationResult<{ api_key: string; key: Consumer["keys"][number] }>>(`/admin-api/gateway/consumers/${id}/keys/rotate`, {
      method: "POST",
    });
  },
  revokeKey(id: number, expectedUpdatedAt: number, reason: string) {
    return request<MutationResult<Consumer["keys"][number]>>(`/admin-api/gateway/consumer-keys/${id}/status`, {
      method: "PATCH",
      body: JSON.stringify({ status: "revoked", expected_updated_at: expectedUpdatedAt, reason, confirm: true }),
    });
  },
  savePolicy(consumerId: number, modelId: number, policy: {
    expected_policy_updated_at: number;
    status: string;
    steps: { step_id?: number; model_id: number; channel_ids: number[]; targets?: Array<{ target_type: "channel" | "pool"; target_id: number }> }[];
    channel_limits?: {
      channel_id: number;
      qps_limit: number;
      max_concurrency: number;
      expected_updated_at?: number;
    }[];
  }) {
    return request<MutationResult<Policy>>(
      `/admin-api/gateway/consumers/${consumerId}/models/${modelId}/policy`,
      { method: "PUT", body: JSON.stringify(policy) },
    );
  },
  circuits(params = "") {
    return request<{ data: CircuitView[] }>(`/admin-api/gateway/circuits${params ? `?${params}` : ""}`);
  },
  updateChannelStatus(channelId: number, status: "enabled" | "disabled", expectedUpdatedAt: number, reason: string) {
    return request<MutationResult<Channel>>(`/admin-api/gateway/channels/${channelId}/status`, {
      method: "PATCH",
      body: JSON.stringify({ status, expected_updated_at: expectedUpdatedAt, reason, confirm: true }),
    });
  },
  probeNow(channelId: number) {
    return request<{ data: unknown }>(`/admin-api/gateway/channels/${channelId}/circuit/probe-now`, {
      method: "POST",
    });
  },
  forceClose(channelId: number, version: number, reason: string) {
    return request<{ data: unknown }>(`/admin-api/gateway/channels/${channelId}/circuit/force-close`, {
      method: "POST",
      body: JSON.stringify({ circuit_version: version, reason, confirm: true }),
    });
  },
  requests(params = "") {
    return request<{ items: GatewayRequestListItem[]; has_more: boolean; next_cursor: string }>(
      `/admin-api/gateway/requests${params ? `?${params}` : ""}`,
    );
  },
  async requestDetail(requestId: string) {
    const response = await request<{ data: GatewayRequestDetail }>(`/admin-api/gateway/requests/${encodeURIComponent(requestId)}`);
    return { ...response, data: { ...response.data, attempts: listOrEmpty(response.data.attempts) } };
  },
  requestPayloads(requestId: string) {
    return request<{ data: GatewayRequestPayloadDetail }>(`/admin-api/gateway/requests/${encodeURIComponent(requestId)}/payloads`);
  },
  dashboard(startAt: number, endAt: number) {
    return request<{ data: DashboardSummary }>(
      `/admin-api/gateway/dashboard/summary?start_at=${startAt}&end_at=${endAt}`,
    );
  },
  async playgroundCapabilities() {
    const response = await request<{ data: PlaygroundCapabilities }>("/admin-api/gateway/playground/capabilities");
    return { ...response, data: { ...response.data, models: listOrEmpty(response.data?.models) } };
  },
  playgroundUpload(file: File) {
    const body = new FormData();
    body.append("file", file);
    return request<{ data: PlaygroundUpload }>("/admin-api/gateway/playground/uploads", { method: "POST", body });
  },
  playgroundSubmitImage(input: PlaygroundImageInput) {
    return request<PlaygroundSubmission>("/admin-api/gateway/playground/images", { method: "POST", body: JSON.stringify(input) });
  },
  playgroundSubmitVideo(input: PlaygroundVideoInput) {
    return request<PlaygroundSubmission>("/admin-api/gateway/playground/videos", { method: "POST", body: JSON.stringify(input) });
  },
  playgroundGeneration(requestId: string) {
    return request<{ data: PlaygroundGeneration }>(`/admin-api/gateway/playground/generations/${encodeURIComponent(requestId)}`);
  },
  async playgroundHistory(scope: PlaygroundHistoryScope, cursor = "", limit = 20) {
    const params = new URLSearchParams({ scope, limit: String(limit) });
    if (cursor) params.set("cursor", cursor);
    const response = await request<{ data: PlaygroundHistoryPage }>(`/admin-api/gateway/playground/history?${params.toString()}`);
    return {
      ...response,
      data: {
        next_cursor: response.data?.next_cursor || "",
        items: listOrEmpty(response.data?.items).map((item) => ({ ...item, result_urls: listOrEmpty(item.result_urls) })),
      },
    };
  },
  async usage(params: URLSearchParams) {
    const response = await request<{ data: UsageReport }>(`/admin-api/gateway/dashboard/usage?${params.toString()}`);
    return {
      ...response,
      data: {
        ...response.data,
        series: listOrEmpty(response.data.series),
        business_breakdown: listOrEmpty(response.data.business_breakdown),
        channel_breakdown: listOrEmpty(response.data.channel_breakdown),
      },
    };
  },
};
